import { Text, View } from '@mirai/ui';
import PropTypes from 'prop-types';
import React, { Children, isValidElement, useEffect, useRef, useState } from 'react';

import * as style from './Typewriter.module.css';

const BASE_DELAY = 60;
const WORD_LENGTH_FACTOR = 5;
const PUNCTUATION_DELAY = 80;
const VARIANCE = 0.2;

const Typewriter = ({ children = '', disabled = false, onTyped, ...others }) => {
  const [displayedNodes, setDisplayedNodes] = useState([]);
  const [wordIndex, setWordIndex] = useState(0);
  const tokensRef = useRef([]);
  const timeoutRef = useRef(null);

  const getWordDelay = (text = '') => {
    let delay = BASE_DELAY;
    const length = typeof text === 'string' ? text.length : 0;
    delay += length * WORD_LENGTH_FACTOR;
    const lastChar = typeof text === 'string' && text[length - 1];
    if (['.', ',', '!', '?', ':', ';'].includes(lastChar)) delay += PUNCTUATION_DELAY;
    const variance = 1 + (Math.random() * VARIANCE * 2 - VARIANCE);
    return Math.round(delay * variance);
  };

  const toTokens = (nodes) => {
    const list = [];
    Children.toArray(nodes).forEach((node) => {
      if (typeof node === 'string') {
        node
          .split(' ')
          .filter(Boolean)
          .forEach((word) => list.push({ node: word, text: word }));
      } else if (isValidElement(node)) {
        list.push({ node, text: typeof node.props?.children === 'string' ? node.props.children : '' });
      }
    });
    return list;
  };

  useEffect(() => {
    tokensRef.current = toTokens(children);
    setDisplayedNodes([]);
    setWordIndex(0);
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [children]);

  useEffect(() => {
    if (disabled) return;

    if (wordIndex < tokensRef.current.length) {
      const current = tokensRef.current[wordIndex];
      const delay = getWordDelay(current.text);
      timeoutRef.current = setTimeout(() => {
        setDisplayedNodes((prev) => {
          if (!prev.length) return [current.node];
          const isPunctuation = typeof current.node === 'string' && /^[.,!?;:]/.test(current.node);
          return isPunctuation ? [...prev, current.node] : [...prev, ' ', current.node];
        });
        setWordIndex((prevIndex) => prevIndex + 1);
        if (wordIndex === tokensRef.current.length - 1 && onTyped) onTyped();
      }, delay);
      return () => clearTimeout(timeoutRef.current);
    }
  }, [disabled, wordIndex, onTyped]);

  return disabled ? (
    <Text {...others}>{children}</Text>
  ) : (
    <View className={[style.typewriter, others.className]}>
      <Text {...others} className={[style.scaffold, others.className]}>
        {children}
      </Text>

      <Text {...others} className={[style.animated, others.className]}>
        {displayedNodes}
      </Text>
    </View>
  );
};

Typewriter.displayName = 'Typewriter';

Typewriter.propTypes = {
  children: PropTypes.node,
  disabled: PropTypes.bool,
  onTyped: PropTypes.func,
};

export { Typewriter };
